<template>
  <v-container fluid class="px-lg-5">
    <app-header
      :title="pageTitle"
      :breadcrumbs="breadcrumbs"
      :showButton="false"
    />
    <v-card class="rounded-card mb-4" flat outlined>
      <v-card-text>
        <div class="text-h6 mb-3">{{ $t('views.appraisals.owner.currentOwners') }}</div>
        <div v-if="!currentHolders.length" class="text-body-1 grey--text">
          {{ $t('views.appraisals.owner.noOwner') }}
        </div>
        <v-chip v-for="holder in currentHolders" :key="holder.id" class="mr-2 mb-2 px-4" large
          color="primary" text-color="white">
          <v-icon left>mdi-account-circle</v-icon>
          <span class="text-body-1">{{ holder.name }}</span>
        </v-chip>
      </v-card-text>
    </v-card>

    <v-card class="rounded-card mb-4" flat outlined>
      <v-card-text>
        <app-field type="checkbox" :label="$t('views.appraisals.owner.changeOwner')" v-model="changeOwner" />

        <app-field v-if="changeOwner" type="select" :label="$t('views.appraisals.owner.selectUser')"
          v-model="selectedUserIds" :options="userOptions" item-text="text" item-value="key"
          multiple chips />
      </v-card-text>
    </v-card>

    <v-row no-gutters>
      <v-col class="col-12 text-right">
        <v-btn class="primary mr-2" :disabled="!canSave || saving" @click="handleSave">{{ $t('actions.save') }}</v-btn>
        <v-btn class="secondary" @click="handleCancel">{{ $t('actions.cancel') }}</v-btn>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import {permissionsList} from 'constants';
import {appendPermissionGrant, appendPermissionDestroy} from '../../../../helpers/permissionAttributes';

export default {
  data() {
    return {
      changeOwner: false,
      saving: false,
      selectedUserIds: [],
      userOptions: [],
      currentHolders: [],
      pageTitle: {
        text: this.$t('layout.mainMenu.appraisalOwners'),
        icon: 'mdi-account-key-outline'
      },
      breadcrumbs: [
        {
          text: this.$t('layout.mainMenu.home'),
          to: {name: 'Home'}
        },
        {
          text: this.$t('layout.mainMenu.appraisals'),
          disabled: true
        },
        {
          text: this.$t('layout.mainMenu.appraisalOwners'),
          to: {name: 'AppraisalOwner'},
          exact: true
        }
      ]
    };
  },
  computed: {
    ...mapGetters(['permission', 'users']),
    canSave() {
      return this.changeOwner && this.selectedUserIds.length > 0;
    }
  },
  methods: {
    ...mapActions(['getPermission', 'getUsers', 'updateUserPermissions']),
    async fetchCurrentOwner() {
      await this.getPermission(permissionsList.ADMINISTER_APPRAISALS);
      if (!this.permission) {
        return;
      }
      this.currentHolders = this.permission.globalHolders;
      this.selectedUserIds = this.currentHolders.map((holder) => `${holder.userId}`);
    },
    async fetchUsers() {
      await this.getUsers({fields: ['firstName', 'lastName'], sortBy: ['firstName']});
      this.userOptions = this._.map(this.users.items, (user) => ({key: `${user.id}`, text: user.name}));
    },
    async handleSave() {
      if (!this.permission || this.saving) {
        return;
      }
      this.saving = true;
      const toRemove = this.currentHolders.filter((holder) => !this.selectedUserIds.includes(`${holder.userId}`));
      const toAdd = this.selectedUserIds.filter(
        (userId) => !this.currentHolders.some((holder) => `${holder.userId}` === userId)
      );

      const removals = toRemove.map((holder) => {
        const data = new FormData();
        appendPermissionDestroy(data, 0, {id: holder.id});
        return this.updateUserPermissions({
          userId: holder.userId, permissions: data, silent: true
        });
      });
      const grants = toAdd.map((userId) => {
        const data = new FormData();
        appendPermissionGrant(data, 0, {permissionId: this.permission.sym});
        return this.updateUserPermissions({
          userId, permissions: data, silent: true
        });
      });
      const responses = await Promise.all([...removals, ...grants]);
      this.saving = false;

      if (!responses.every((response) => this.successCode(response.status))) {
        this.$snotify.error(this.$t('messages.error.updated', {entity: this.$tc('models.user.entity', 1)}));
        await this.fetchCurrentOwner();
        return;
      }
      this.$snotify.success(this.$t('messages.success.updated', {entity: this.$tc('models.user.entity', 1)}));
      this.$router.push({name: 'Home'});
    },
    handleCancel() {
      this.$router.push({name: 'Home'});
    }
  },
  created() {
    this.fetchCurrentOwner();
    this.fetchUsers();
  }
};
</script>
